// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// App shell: header, nav routing, glossary popovers, About modal, state drawer.
import './styles.css';
import { loadData, type Loaded } from './data';
import { initTooltip } from './components/tooltip';
import { GLOSSARY } from './glossary';
import {
  renderOverview, renderFertility, renderAges, renderMapView, teardownMap,
  renderNuptiality, renderSeasonality, renderExplorer, renderInsights,
  stateDrawerHtml, type Ctx,
} from './views';

interface ViewDef { key: string; label: string; render: (root: HTMLElement, ctx: Ctx) => void; hasMap?: boolean }
const VIEWS: ViewDef[] = [
  { key: 'overview', label: 'Overview', render: renderOverview },
  { key: 'fertility', label: 'Fertility rate', render: renderFertility },
  { key: 'ages', label: 'Older mothers', render: renderAges },
  { key: 'map', label: 'By state', render: renderMapView, hasMap: true },
  { key: 'nuptiality', label: 'Marriage', render: renderNuptiality },
  { key: 'seasonality', label: 'Seasonality', render: renderSeasonality },
  { key: 'explorer', label: 'Explore', render: renderExplorer },
  { key: 'insights', label: 'Insights', render: renderInsights },
];

const app = document.getElementById('app')!;

function boot(): void {
  app.innerHTML = `<div class="loading"><div class="spinner"></div><p>Loading Australia's birth data…</p></div>`;
  loadData().then(start).catch((err) => {
    app.innerHTML = `<div class="errorbox"><h2>Could not load the data</h2><p>${String(err)}</p><button onclick="location.reload()">Retry</button></div>`;
  });
}

function start(loaded: Loaded): void {
  const { data, meta } = loaded;
  const ctx: Ctx = { data, meta, openState: (code) => openDrawer(code) };
  let current = '';

  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#overview" aria-label="Births & Fertility home">
          <span class="brand-mark" aria-hidden="true"></span>
          <span class="brand-text"><strong>Births &amp; Fertility</strong><small>Australia · ABS ${meta.dataFrom}–${meta.dataThrough}</small></span>
        </a>
        <nav class="tabs" id="tabs" aria-label="Views">
          ${VIEWS.map((v) => `<button class="tab" data-view="${v.key}">${v.label}</button>`).join('')}
        </nav>
        <button class="about-btn" id="about-btn" aria-label="About this site">?</button>
      </div>
    </header>
    <main class="main-content" id="main"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-src">Source: <a href="https://www.abs.gov.au/statistics/people/population/births-australia/latest-release" target="_blank" rel="noopener">ABS Births, Australia</a> (CC BY 4.0). Data to ${meta.dataThrough}. Not affiliated with the ABS.</div>
        <div class="footer-by">Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>
    <div class="drawer-scrim" id="scrim" hidden></div>
    <aside class="state-drawer" id="drawer" hidden aria-label="State detail"><button class="drawer-close" id="drawer-close" aria-label="Close">×</button><div id="drawer-content"></div></aside>
    <div class="modal-scrim" id="about-scrim" hidden></div>
    <div class="about-modal" id="about-modal" hidden role="dialog" aria-modal="true" aria-labelledby="about-title"></div>
    <div class="gloss-pop" id="gloss-pop" hidden role="dialog"></div>`;

  const main = document.getElementById('main')!;
  const tabs = document.getElementById('tabs')!;

  function navigate(key: string, replace = false): void {
    const view = VIEWS.find((v) => v.key === key) ?? VIEWS[0];
    if (current === 'map' && view.key !== 'map') teardownMap();
    current = view.key;
    tabs.querySelectorAll('.tab').forEach((t) => t.classList.toggle('active', (t as HTMLElement).dataset.view === view.key));
    main.scrollTop = 0; window.scrollTo(0, 0);
    document.querySelector('.hover-tip')?.classList.remove('visible'); // clear any stale tooltip
    main.innerHTML = '';
    view.render(main, ctx);
    const hash = `#${view.key}`;
    if (replace) history.replaceState(null, '', hash); else if (location.hash !== hash) history.pushState(null, '', hash);
    document.title = `${view.label} · Births & Fertility — Australia`;
  }

  tabs.querySelectorAll('.tab').forEach((t) => t.addEventListener('click', () => navigate((t as HTMLElement).dataset.view!)));

  // Routing from hash (supports #view and #s=CODE).
  function fromHash(replace = false): void {
    const h = location.hash.replace(/^#/, '');
    const stateMatch = h.match(/^s=([A-Z]+)$/);
    if (stateMatch) { if (!current) navigate('map', true); openDrawer(stateMatch[1]); return; }
    const key = VIEWS.some((v) => v.key === h) ? h : 'overview';
    navigate(key, replace);
  }
  window.addEventListener('popstate', () => {
    const h = location.hash.replace(/^#/, '');
    const stateMatch = h.match(/^s=([A-Z]+)$/);
    if (stateMatch) { openDrawer(stateMatch[1]); return; }
    closeDrawer(true);
    const key = VIEWS.some((v) => v.key === h) ? h : 'overview';
    if (key !== current) navigate(key, true);
  });

  // ── State drawer ──────────────────────────────────────────────────────────
  const drawer = document.getElementById('drawer')!;
  const scrim = document.getElementById('scrim')!;
  const drawerContent = document.getElementById('drawer-content')!;
  function openDrawer(code: string): void {
    if (!data.summary[code]) return;
    drawerContent.innerHTML = stateDrawerHtml(data, meta, code);
    drawer.hidden = false; scrim.hidden = false;
    void drawer.offsetWidth; // force reflow so the transform transition fires (rAF is throttled in background tabs)
    drawer.classList.add('open'); scrim.classList.add('show');
  }
  function closeDrawer(silent = false): void {
    drawer.classList.remove('open'); scrim.classList.remove('show');
    setTimeout(() => { drawer.hidden = true; scrim.hidden = true; drawerContent.innerHTML = ''; }, 260);
    if (!silent && /^#s=/.test(location.hash)) history.replaceState(null, '', `#${current}`);
  }
  document.getElementById('drawer-close')!.addEventListener('click', () => closeDrawer());
  scrim.addEventListener('click', () => closeDrawer());

  // ── About modal ───────────────────────────────────────────────────────────
  const aboutModal = document.getElementById('about-modal')!;
  const aboutScrim = document.getElementById('about-scrim')!;
  aboutModal.innerHTML = aboutHtml(meta);
  function openAbout(): void { aboutModal.hidden = false; aboutScrim.hidden = false; void aboutModal.offsetWidth; aboutModal.classList.add('open'); aboutScrim.classList.add('show'); }
  function closeAbout(): void { aboutModal.classList.remove('open'); aboutScrim.classList.remove('show'); setTimeout(() => { aboutModal.hidden = true; aboutScrim.hidden = true; }, 220); }
  document.getElementById('about-btn')!.addEventListener('click', openAbout);
  aboutScrim.addEventListener('click', closeAbout);
  aboutModal.addEventListener('click', (e) => { if ((e.target as HTMLElement).id === 'about-close') closeAbout(); });

  // ── Glossary popover ──────────────────────────────────────────────────────
  const glossPop = document.getElementById('gloss-pop')!;
  function showGloss(el: HTMLElement): void {
    const key = el.dataset.term!; const g = GLOSSARY[key];
    if (!g) return;
    glossPop.innerHTML = `<strong>${g.term}</strong><p>${g.definition}</p>`;
    glossPop.hidden = false;
    const r = el.getBoundingClientRect();
    const pw = Math.min(320, window.innerWidth - 24);
    glossPop.style.width = `${pw}px`;
    let left = r.left; if (left + pw > window.innerWidth - 12) left = window.innerWidth - pw - 12;
    glossPop.style.left = `${Math.max(12, left)}px`;
    glossPop.style.top = `${r.bottom + window.scrollY + 8}px`;
    glossPop.classList.add('show');
  }
  function hideGloss(): void { glossPop.hidden = true; glossPop.classList.remove('show'); }
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const link = t.closest('.gloss-link') as HTMLElement | null;
    if (link) { e.stopPropagation(); if (!glossPop.hidden && glossPop.dataset.for === link.dataset.term) { hideGloss(); } else { glossPop.dataset.for = link.dataset.term; showGloss(link); } return; }
    if (!glossPop.hidden && !glossPop.contains(t)) hideGloss();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { hideGloss(); closeAbout(); if (!drawer.hidden) closeDrawer(); }
    const t = e.target as HTMLElement;
    if ((e.key === 'Enter' || e.key === ' ') && t.classList.contains('gloss-link')) { e.preventDefault(); glossPop.dataset.for = t.dataset.term; showGloss(t); }
  });
  window.addEventListener('resize', hideGloss);
  window.addEventListener('scroll', () => { if (!glossPop.hidden) hideGloss(); }, { passive: true });

  initTooltip();
  fromHash(true);
}

function aboutHtml(meta: { dataFrom: number; dataThrough: number }): string {
  return `<div class="about-inner">
    <button class="modal-close" id="about-close" aria-label="Close">×</button>
    <h2 id="about-title">About Births &amp; Fertility</h2>
    <p>This site turns fifty years of Australian birth registrations into one explorer. It answers the questions behind the headlines: how far has the birth rate really fallen, how much later are people having children, where is fertility highest, and when are babies actually born?</p>
    <h3>The data</h3>
    <p>Everything comes from the Australian Bureau of Statistics release <em>Births, Australia</em>, fetched from the ABS Data API (SDMX). Four tables are combined:</p>
    <ul>
      <li><strong>Summary</strong> — total births, crude birth rate and nuptial split, by state, ${meta.dataFrom} onwards.</li>
      <li><strong>Fertility by age</strong> — age-specific fertility rates and the total fertility rate, by state.</li>
      <li><strong>Confinements</strong> — median age of mothers, first-time mothers and fathers.</li>
      <li><strong>Month of occurrence</strong> — births by calendar month, for the seasonality view.</li>
    </ul>
    <h3>How it's checked</h3>
    <p>The data pipeline enforces three reconciliation gates before publishing: the two independent "all births" columns must agree exactly; the published total fertility rate must equal the sum of its age-specific parts; and nuptial plus ex-nuptial births must account for the total. Agreement across independently published tables is what proves a correct read.</p>
    <h3>Things to know</h3>
    <ul>
      <li>Births are counted by <strong>year of registration</strong> and attributed to the mother's <strong>usual residence</strong>.</li>
      <li>Around 7% of births in some small states have the mother's age not stated; those are excluded from the age breakdown but not the totals.</li>
      <li>The seasonality view uses <strong>month of occurrence</strong> and excludes the latest year, whose recent births are still being registered.</li>
      <li>Data updates roughly annually, when the ABS publishes the previous year.</li>
    </ul>
    <p class="about-src">Data © Australian Bureau of Statistics, licensed CC BY 4.0. This site is independent and not affiliated with the ABS.</p>
  </div>`;
}

boot();
